import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Collapse,
  Empty,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  EditOutlined,
  GroupOutlined,
  LeftOutlined,
  HolderOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ListButton } from "@refinedev/antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { axiosInstance } from "@/utilities";
import { PaginationTotal } from "@/components/pagination-total";
import {
  API_URL_PIM_ATTRIBUTES,
  API_URL_PIM_ATTRIBUTE_SETS,
} from "@/services/XCONS_ENV";
import type {
  AttributeRow,
  AttributeSetDetailDto,
  AttributeSetGroupDto,
  AttributeSetRow,
} from "../types";
import { HistorialDelCatalogo } from "../HistorialDelCatalogo";
import { FILAS_POR_PAGINA, OPCIONES_POR_PAGINA } from "@/config/paginacion";

const { Text, Title } = Typography;

/**
 * La flecha de volver lleva a la pantalla de la que se vino, no a una fija
 * (xcons-docs#408). Si la página se abrió directo por URL no hay pantalla anterior
 * dentro del admin —React Router marca esa entrada con `key === "default"`—, y ahí
 * vuelve a la pantalla de arriba.
 */
const useVolver = (arriba: string) => {
  const navigate = useNavigate();
  const location = useLocation();
  return () =>
    location.key === "default" ? navigate(arriba) : navigate(-1);
};

/** Una fila arrastrable del editor. El handle es el ícono, no la fila: si no, no se puede clickear. */
const FilaArrastrable: React.FC<{
  id: string;
  children: React.ReactNode;
  extra?: React.ReactNode;
}> = ({ id, children, extra }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  return (
    <Flex
      ref={setNodeRef}
      align="center"
      gap={8}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        background: isDragging ? "#f0f5ff" : "#fff",
        border: "1px solid #f0f0f0",
        borderRadius: 6,
        padding: "6px 10px",
        marginBottom: 6,
      }}
      {...attributes}
    >
      <HolderOutlined {...listeners} style={{ cursor: "grab", color: "#8c8c8c" }} />
      <div style={{ flex: 1 }}>{children}</div>
      {extra}
    </Flex>
  );
};

/**
 * Editor de un conjunto de atributos (PIM Fase 16).
 *
 * **El drag reordena DENTRO de cada grupo**, que es lo que el diseño pide (C2.1) y lo que
 * define el orden de los campos en el form de producto. Mover un atributo de grupo se hace
 * con el select de la fila y no arrastrando entre listas: un drag entre contenedores es una
 * interacción bastante más frágil, y el resultado que importa —qué sección lo muestra— se
 * expresa mejor eligiéndolo.
 *
 * **El orden se guarda entero y explícito**, con un botón. No se auto-guarda cada movimiento:
 * un drag renumera todas las filas de abajo, así que son N updates por gesto, y a mitad de
 * reordenar una sección el estado intermedio no es algo que valga la pena persistir.
 *
 * **No hay forma de sacar un atributo del conjunto.** Es deliberado: un atributo sacado deja
 * huérfanos los valores que los productos de ese conjunto ya tenían cargados. La fase crea y
 * edita, no borra.
 */
export const CatalogAttributeSetEditor: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const volver = useVolver("/catalog/attribute-sets");

  const [detalle, setDetalle] = useState<AttributeSetDetailDto | null>(null);
  const [grupos, setGrupos] = useState<AttributeSetGroupDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [sucio, setSucio] = useState(false);

  const [grupoNuevo, setGrupoNuevo] = useState(false);
  /** El grupo que se está renombrando (C2.2): los grupos se crean y también se renombran. */
  const [renombrando, setRenombrando] = useState<{
    groupId: number;
    name: string;
  } | null>(null);
  const [asignando, setAsignando] = useState(false);
  const [disponibles, setDisponibles] = useState<AttributeRow[]>([]);
  const [formGrupo] = Form.useForm();
  const [formAsignar] = Form.useForm();

  const sensors = useSensors(
    // 6px de tolerancia: sin esto un click en el handle empieza un drag de 0px y la fila
    // "salta" sin que nadie la haya movido.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get<AttributeSetDetailDto>(
        `${API_URL_PIM_ATTRIBUTE_SETS}/${encodeURIComponent(id)}/groups`,
      );
      setDetalle(data);
      setGrupos(data.groups);
      setSucio(false);
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo cargar el conjunto",
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const alSoltar = (groupId: number) => (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setGrupos((prev) =>
      prev.map((g) => {
        if (g.groupId !== groupId) return g;
        const desde = g.attributes.findIndex((a) => a.code === active.id);
        const hasta = g.attributes.findIndex((a) => a.code === over.id);
        if (desde < 0 || hasta < 0) return g;
        return { ...g, attributes: arrayMove(g.attributes, desde, hasta) };
      }),
    );
    setSucio(true);
  };

  const moverDeGrupo = (code: string, destino: number) => {
    setGrupos((prev) => {
      const origen = prev.find((g) => g.attributes.some((a) => a.code === code));
      const attr = origen?.attributes.find((a) => a.code === code);
      if (!origen || !attr || origen.groupId === destino) return prev;

      return prev.map((g) => {
        if (g.groupId === origen.groupId) {
          return {
            ...g,
            attributes: g.attributes.filter((a) => a.code !== code),
          };
        }
        if (g.groupId === destino) {
          return { ...g, attributes: [...g.attributes, attr] };
        }
        return g;
      });
    });
    setSucio(true);
  };

  /**
   * Manda el orden COMPLETO del conjunto, no lo que se movió: la posición de una fila es
   * relativa a todas las demás, y el backend lo aplica en una transacción.
   *
   * Las posiciones se renumeran de 10 en 10 para dejar lugar entre medio, que es lo que hace
   * Magento con `sort_order` y lo que permite insertar sin renumerar todo.
   */
  const guardarOrden = async () => {
    setGuardando(true);
    try {
      const items = grupos.flatMap((g) =>
        g.attributes.map((a, i) => ({
          code: a.code,
          attributeGroupId: g.groupId,
          sortOrder: (i + 1) * 10,
        })),
      );
      const { data } = await axiosInstance.put<{ updated: number }>(
        `${API_URL_PIM_ATTRIBUTE_SETS}/${encodeURIComponent(id)}/order`,
        { items },
      );
      message.success(`Orden guardado (${data.updated} atributos)`);
      await cargar();
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo guardar el orden",
      );
    } finally {
      setGuardando(false);
    }
  };

  const crearGrupo = async () => {
    const values = await formGrupo.validateFields();
    try {
      await axiosInstance.post(
        `${API_URL_PIM_ATTRIBUTE_SETS}/${encodeURIComponent(id)}/groups`,
        values,
      );
      setGrupoNuevo(false);
      formGrupo.resetFields();
      await cargar();
      message.success("Grupo creado");
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo crear el grupo",
      );
    }
  };

  const renombrarGrupo = async () => {
    if (!renombrando) return;
    const name = renombrando.name.trim();
    if (!name) return;
    try {
      await axiosInstance.patch(
        `${API_URL_PIM_ATTRIBUTE_SETS}/${encodeURIComponent(id)}/groups/${renombrando.groupId}`,
        { name },
      );
      setRenombrando(null);
      await cargar();
      message.success("Grupo renombrado");
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo renombrar el grupo",
      );
    }
  };

  const abrirAsignar = async () => {
    setAsignando(true);
    try {
      const { data } = await axiosInstance.get<AttributeRow[]>(
        API_URL_PIM_ATTRIBUTES,
      );
      // Los que ya están en el conjunto no se ofrecen: asignarlos otra vez es un no-op.
      const yaEstan = new Set(
        grupos.flatMap((g) => g.attributes.map((a) => a.code)),
      );
      setDisponibles(data.filter((a) => !yaEstan.has(a.code)));
    } catch {
      setDisponibles([]);
    }
  };

  const asignar = async () => {
    const values = await formAsignar.validateFields();
    try {
      await axiosInstance.post(
        `${API_URL_PIM_ATTRIBUTE_SETS}/${encodeURIComponent(id)}/attributes`,
        values,
      );
      setAsignando(false);
      formAsignar.resetFields();
      await cargar();
      message.success("Atributo asignado");
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo asignar",
      );
    }
  };

  if (loading) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 10 }} />
      </Card>
    );
  }

  if (!detalle) return null;

  const visibles = grupos.reduce((n, g) => n + g.attributes.length, 0);

  return (
    <>
      {/* El título va afuera de la Card, con el mismo tamaño que el resto de las
        secciones, y las acciones en su misma línea (xcons-docs#408). */}
      <Flex
        align="center"
        justify="space-between"
        gap={8}
        wrap
        style={{ marginBottom: 24 }}
      >
        <Flex align="center" gap={8}>
          <ListButton hideText icon={<LeftOutlined />} onClick={volver} />
          <Title level={3} style={{ marginBottom: 0 }}>
            {detalle.name}
          </Title>
          <Tag>{detalle.productCount.toLocaleString("es-AR")} productos</Tag>
          <Tag>{visibles} atributos</Tag>
        </Flex>
        <Space>
          <Button icon={<PlusOutlined />} onClick={() => setGrupoNuevo(true)}>
            Nuevo grupo
          </Button>
          <Button icon={<PlusOutlined />} onClick={() => void abrirAsignar()}>
            Asignar atributo
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            disabled={!sucio}
            loading={guardando}
            onClick={() => void guardarOrden()}
          >
            Guardar orden
          </Button>
        </Space>
      </Flex>
      {/* Sin Card: las secciones van directo sobre el fondo, sin el borde blanco
        alrededor (xcons-docs#408). */}
      <div>
        <Flex vertical gap={16}>
          {detalle.groups.length === 0 ? (
            <Empty description="Este conjunto no tiene grupos" />
          ) : (
            <Collapse
              defaultActiveKey={grupos.slice(0, 3).map((g) => String(g.groupId))}
              items={grupos.map((g) => ({
                key: String(g.groupId),
                label: (
                  <Space>
                    <Text strong>{g.groupName}</Text>
                    <Text type="secondary">({g.attributes.length})</Text>
                  </Space>
                ),
                extra: (
                  <Tooltip title="Renombrar la sección">
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      // Sin esto el click también abre o cierra la sección.
                      onClick={(e) => {
                        e.stopPropagation();
                        setRenombrando({ groupId: g.groupId, name: g.groupName ?? "" });
                      }}
                    />
                  </Tooltip>
                ),
                children: g.attributes.length ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={alSoltar(g.groupId)}
                  >
                    <SortableContext
                      items={g.attributes.map((a) => a.code)}
                      strategy={verticalListSortingStrategy}
                    >
                      {g.attributes.map((a) => (
                        <FilaArrastrable
                          key={a.code}
                          id={a.code}
                          extra={
                            <Space>
                              <Tag>{a.frontendInput ?? "—"}</Tag>
                              {a.isRequired && <Tag color="red">obligatorio</Tag>}
                              <Tooltip title="Mover a otra sección del form">
                                <Select
                                  size="small"
                                  style={{ minWidth: 180 }}
                                  value={g.groupId}
                                  onChange={(destino) => moverDeGrupo(a.code, destino)}
                                  options={grupos.map((o) => ({
                                    value: o.groupId,
                                    label: o.groupName,
                                  }))}
                                />
                              </Tooltip>
                            </Space>
                          }
                        >
                          <Space size={4}>
                            <Text strong>{a.label ?? a.code}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {a.code}
                            </Text>
                          </Space>
                        </FilaArrastrable>
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  <Text type="secondary">
                    Sección vacía. Se le puede mover un atributo desde otra.
                  </Text>
                ),
              }))}
            />
          )}

          {/* El historial del conjunto (xcons-docs#436) debajo de las secciones: es consulta,
              no parte del form. Cada acción de la pantalla recarga con `cargar`, que pasa por
              el skeleton, así que la línea de tiempo se vuelve a pedir con el evento nuevo. */}
          <HistorialDelCatalogo module="attribute_set" entityId={id} />
        </Flex>
      </div>

      <Modal
        open={grupoNuevo}
        title="Nuevo grupo"
        okText="Crear"
        cancelText="Cancelar"
        onOk={() => void crearGrupo()}
        onCancel={() => setGrupoNuevo(false)}
      >
        <Form form={formGrupo} layout="vertical">
          <Form.Item
            name="name"
            label="Nombre de la sección"
            rules={[{ required: true, message: "El nombre es obligatorio" }]}
          >
            <Input placeholder="Datos técnicos" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={renombrando !== null}
        title="Renombrar grupo"
        okText="Guardar"
        cancelText="Cancelar"
        okButtonProps={{ disabled: !renombrando?.name.trim() }}
        onOk={() => void renombrarGrupo()}
        onCancel={() => setRenombrando(null)}
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
          Es el título de la sección en el form de producto. Los atributos siguen en el
          mismo grupo.
        </Text>
        <Input
          autoFocus
          value={renombrando?.name}
          onChange={(e) =>
            setRenombrando((r) => (r ? { ...r, name: e.target.value } : r))
          }
          onPressEnter={() => void renombrarGrupo()}
        />
      </Modal>

      <Modal
        open={asignando}
        title="Asignar un atributo al conjunto"
        okText="Asignar"
        cancelText="Cancelar"
        onOk={() => void asignar()}
        onCancel={() => setAsignando(false)}
      >
        <Form form={formAsignar} layout="vertical">
          <Form.Item
            name="code"
            label="Atributo"
            rules={[{ required: true, message: "Elegí un atributo" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Buscar por código o etiqueta"
              options={disponibles.map((a) => ({
                value: a.code,
                label: `${a.label ?? a.code} · ${a.code}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="attributeGroupId"
            label="Sección"
            rules={[{ required: true, message: "Elegí la sección" }]}
          >
            <Select
              options={grupos.map((g) => ({
                value: g.groupId,
                label: g.groupName,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

/** Listado de conjuntos (PIM Fase 16). */
export const CatalogAttributeSetsList: React.FC = () => {
  const navigate = useNavigate();
  const volver = useVolver("/catalog/attributes");
  const [rows, setRows] = useState<AttributeSetRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [creando, setCreando] = useState(false);
  const [search, setSearch] = useState("");
  const [form] = Form.useForm();

  /**
   * La búsqueda es en el cliente: el endpoint ya trae todos los conjuntos, y son pocos
   * (xcons-docs#427). Sin distinguir mayúsculas ni tildes.
   */
  const visibles = useMemo(() => {
    const normalizar = (s: string) =>
      s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
    const q = normalizar(search.trim());
    return q ? rows.filter((r) => normalizar(r.name ?? "").includes(q)) : rows;
  }, [rows, search]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get<AttributeSetRow[]>(
        API_URL_PIM_ATTRIBUTE_SETS,
      );
      setRows(data);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const crear = async () => {
    const values = await form.validateFields();
    try {
      const { data } = await axiosInstance.post<{ attributeSetId: number }>(
        API_URL_PIM_ATTRIBUTE_SETS,
        values,
      );
      setCreando(false);
      form.resetFields();
      message.success("Conjunto creado");
      navigate(`/catalog/attribute-sets/${data.attributeSetId}`);
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo crear el conjunto",
      );
    }
  };

  return (
    <>
      {/* El título va afuera de la tabla, con el mismo tamaño que el de Atributos y el
        del resto de las secciones (xcons-docs#408). */}
      <Flex align="center" gap={8} style={{ marginBottom: 24 }}>
        <ListButton hideText icon={<LeftOutlined />} onClick={volver} />
        {/* Ícono propio, distinto al de Atributos (xcons-docs#427). */}
        <GroupOutlined style={{ fontSize: 20 }} />
        <Title level={3} style={{ marginBottom: 0 }}>
          Conjuntos de atributos
        </Title>
      </Flex>
      {/* Sin Card, igual que Atributos y Productos: la tabla llega sola a los bordes y
        la acción va en su `title`. Sin botón a Atributos: a Atributos se vuelve con la
        flecha (xcons-docs#408). */}
      <Table<AttributeSetRow>
        rowKey="attributeSetId"
        size="small"
        loading={loading}
        dataSource={visibles}
        // El mismo paginador que Atributos: de tamaño normal aunque la tabla sea
        // `small`, con el total y el selector de cantidad (xcons-docs#427).
        pagination={{
          size: "default",
          defaultPageSize: FILAS_POR_PAGINA,
          showSizeChanger: true,
          pageSizeOptions: OPCIONES_POR_PAGINA,
          showTotal: (t) => (
            <PaginationTotal total={t} entityName="conjuntos" />
          ),
        }}
        onRow={(row) => ({
          style: { cursor: "pointer" },
          onClick: () => navigate(`/catalog/attribute-sets/${row.attributeSetId}`),
        })}
        title={() => (
          // Buscador y acción en una línea, como en Atributos (xcons-docs#427).
          <Flex gap={8}>
            <Input.Search
              allowClear
              style={{ flex: 1 }}
              placeholder="Buscar por nombre"
              defaultValue={search}
              onSearch={setSearch}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreando(true)}
            >
              Nuevo conjunto
            </Button>
          </Flex>
        )}
        columns={[
          { title: "Conjunto", dataIndex: "name", key: "name", width: "50%" },
          {
            title: "Productos",
            dataIndex: "productCount",
            key: "productCount",
            width: "50%",
            render: (n: number) => n.toLocaleString("es-AR"),
          },
        ]}
      />

      <Modal
        open={creando}
        title="Nuevo conjunto"
        okText="Crear"
        cancelText="Cancelar"
        onOk={() => void crear()}
        onCancel={() => setCreando(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Nombre"
            rules={[{ required: true, message: "El nombre es obligatorio" }]}
          >
            <Input placeholder="Aberturas" />
          </Form.Item>
          <Form.Item
            name="basedOnSetId"
            label="Basado en (opcional)"
            extra="Se copian sus grupos y sus atributos. Sin elegir ninguno, el conjunto arranca con SKU y nombre."
          >
            <Select
              allowClear
              placeholder="Ninguno"
              options={rows.map((r) => ({
                value: r.attributeSetId,
                label: r.name,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
